<script>
import axios from 'axios';
import { swalResponse, notify } from '@/utils.js';

function getEmpty() {
    return {
        name: '',
        email: '',
        phone: '',
        message: '',
        privacy: false
    };
}

function formatContactQuery(formData) {
    return {
        working_context: 'general',
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
    };
}

export default {
    name: 'ContactForm',

    props: {
        debug: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        isProcessing: false,
        formData: getEmpty()
    }),

    methods: {
        async submitForm() {
            this.isProcessing = true;

            try {
                const response = await axios.post(
                    'contacto',
                    formatContactQuery(this.formData)
                );

                swalResponse(response);

                if (response && response.status) {
                    this.formData = getEmpty();
                }
            } catch (e) {
                console.error(
                    'Error in submitForm:',
                    e
                );

                notify(
                    'Error submitting form',
                    'error'
                );
            } finally {
                this.isProcessing = false;
            }
        }
    }
};
</script>

<template>
    <form
        id="contact-form"
        @submit.prevent="submitForm"
    >

        <div class="form__heading">

            <span class="form__heading-label">
                {{ 'contact_form_header' | staticTitle }}
            </span>

            <vue-marked>
                {{ 'contact_form_header' | staticContent }}
            </vue-marked>

        </div>

        <div class="form__group">

            <label for="name">
                {{ 'contact_form_name' | staticTitle }} *
            </label>

            <input
                id="name"
                v-model="formData.name"
                type="text"
                name="name"
                :placeholder="$options.filters.staticContent('contact_form_name')"
                required
            >

        </div>

        <div class="form__row">

            <div class="form__group">

                <label for="email">
                    {{ 'contact_form_email' | staticTitle }} *
                </label>

                <input
                    id="email"
                    v-model="formData.email"
                    type="email"
                    name="email"
                    :placeholder="$options.filters.staticContent('contact_form_email')"
                    required
                >

            </div>

            <div class="form__group">

                <label for="phone">
                    {{ 'contact_form_phone' | staticTitle }}
                </label>

                <input
                    id="phone"
                    v-model="formData.phone"
                    type="tel"
                    name="phone"
                    :placeholder="$options.filters.staticContent('contact_form_phone')"
                >

            </div>

        </div>

        <div class="form__group">

            <label for="message">
                {{ 'contact_form_message' | staticTitle }} *
            </label>

            <textarea
                id="message"
                v-model="formData.message"
                name="message"
                rows="6"
                :placeholder="$options.filters.staticContent('contact_form_message')"
                required
            ></textarea>

        </div>

        <div class="form__checkbox">

            <input
                id="privacy"
                v-model="formData.privacy"
                type="checkbox"
                required
            >

            <label for="privacy">

                {{ 'contact_form_legal' | staticTitle }}

                <router-link :to="{ name: 'PrivacyPolicy' }" target="_blank">
                    {{ 'contact_form_legal' | staticContent }}
                </router-link>

                {{ 'contact_form_actions' | staticContentAlt }}

                <router-link :to="{ name: 'TermsConditions' }" target="_blank" >
                    {{ 'contact_form_legal' | staticContentAlt }}
                </router-link>.

            </label>

        </div>

        <button
            type="submit"
            class="form__submit"
            :disabled="isProcessing"
        >

            <span>
                {{
                    isProcessing
                        ? $options.filters.staticContent('contact_form_actions')
                        : $options.filters.staticTitle('contact_form_actions')
                }}
            </span>

            <span class="form__submit-arrow">
                ↗
            </span>

        </button>

        <pre v-if="debug">
            {{ formData }}
        </pre>

    </form>
</template>

<style lang="scss" scoped>
#contact-form {
    display: flex;
    flex-direction: column;

    width: 100%;

    padding: 1.625em;

    background: #FFFFFF;

    color: #111827;
}

.form__heading {
    margin-bottom: 2.125em;

    padding-bottom: 1.5em;

    border-bottom:
        0.0625em solid
        rgba(1, 16, 36, .10);

    &-label {
        display: block;

        margin-bottom: 0.625em;

        color: var(--color-3);

        font-family: var(--font-tech);
        font-size: 0.6875em;
        font-weight: 600;
        -letter-spacing: 0.125em;
        -letter-spacing: 0;

        text-transform: uppercase;
    }

    p {
        max-width: 26.25em;

        margin: 0;

        color: #667085;

        font-size: 0.875em;
        line-height: 1.6;
    }
}

.form__row {
    display: grid;
    grid-template-columns: 1fr;

    gap: 0;
}

.form__group {
    display: flex;
    flex-direction: column;

    margin-bottom: 1.625em;

    label {
        margin-bottom: 0.5625em;

        color: #182230;

        font-family: var(--font-tech);
        font-size: 0.6875em;
        font-weight: 600;
        -letter-spacing: 0.0625em;
        -letter-spacing: 0;

        text-transform: uppercase;
    }

    input,
    textarea {
        width: 100%;

        padding: 0.875em 1em;

        border:
            0.0625em solid
            rgba(1, 16, 36, .14);

        border-radius: 0.5em;

        background: #F8FAFC;

        color: #111827;

        font-family: var(--font-main);
        font-size: 0.9375em;

        outline: none;

        transition:
            border-color .3s ease,
            background .3s ease,
            box-shadow .3s ease;

        &::placeholder {
            color: #98A2B3;
        }

        &:hover {
            border-color:
                rgba(1, 16, 36, .24);
        }

        &:focus {
            border-color: var(--color-3);

            background: #FFFFFF;

            box-shadow:
                0 0 0 0.1875em
                rgba(233, 81, 29, .10);
        }
    }

    textarea {
        min-height: 9.6875em;

        resize: vertical;

        line-height: 1.6;
    }
}

.form__checkbox {
    display: grid;
    grid-template-columns: 1.25em 1fr;

    gap: 0.75em;

    align-items: start;

    margin: 0.125em 0 1.75em;

    input {
        width: 1.0625em;
        height: 1.0625em;

        margin-top: 0.1875em;

        accent-color: var(--color-3);

        cursor: pointer;
    }

    label {
        color: #667085;

        font-size: 0.75em;
        line-height: 1.6;

        cursor: pointer;
    }

    a {
        color: var(--color-3);

        font-weight: 600;

        text-decoration: none;

        border-bottom:
            0.0625em solid
            rgba(233, 81, 29, .30);

        transition:
            color .3s ease,
            border-color .3s ease;

        &:hover {
            color: #C64014;

            border-color: #C64014;
        }
    }
}

.form__submit {
    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 1.5em;

    width: 100%;

    min-height: 3.625em;

    padding: 0 1.375em;

    border: 0.0625em solid var(--color-3);

    background: var(--color-3);

    color: #FFFFFF;

    font-family: var(--font-tech);
    font-size: 0.75em;
    font-weight: 600;
    -letter-spacing: 0.09375em;
    -letter-spacing: 0;

    cursor: pointer;

    transition:
        background .3s ease,
        border-color .3s ease,
        transform .3s ease,
        box-shadow .3s ease;

    &:hover:not(:disabled) {
        background: #D74617;
        border-color: #D74617;

        transform: translateY(-0.125em);

        box-shadow:
            0 0.75em 1.75em
            rgba(233, 81, 29, .20);
    }

    &:disabled {
        opacity: .55;

        cursor: not-allowed;
    }
}

.form__submit-arrow {
    font-size: 1.125em;

    transition: transform .3s ease;
}

.form__submit:hover:not(:disabled)
.form__submit-arrow {
    transform:
        translate(0.1875em, -0.1875em);
}

pre {
    margin-top: 1.875em;

    white-space: pre-wrap;
}

@media (min-width: 43.75em) {
    #contact-form {
        padding: 2.125em;
    }

    .form__row {
        grid-template-columns:
            repeat(2, minmax(0, 1fr));

        gap: 1.375em;
    }
}

@media (min-width: 68.75em) {
    #contact-form {
        padding: 2.5em;
    }
}
</style>